import React, { useEffect, useState } from "react";
import {
  ref,
  listAll,
  getDownloadURL,
  deleteObject,
  StorageReference,
} from "firebase/storage";
import { storage } from "@/firebase/config";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import {
  Loader2,
  Trash2,
  Image as ImageIcon,
  Copy,
  ExternalLink,
  RefreshCw,
} from "lucide-react";

interface StorageImage {
  name: string;
  fullPath: string;
  url: string;
  ref: StorageReference;
}

const NewsletterImages = () => {
  const { toast } = useToast();
  const [images, setImages] = useState<StorageImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const fetchImages = async () => {
    setLoading(true);
    try {
      const listRef = ref(storage, "newsletter-images");
      const res = await listAll(listRef);

      const imagePromises = res.items.map(async (itemRef) => {
        const url = await getDownloadURL(itemRef);
        return {
          name: itemRef.name,
          fullPath: itemRef.fullPath,
          url,
          ref: itemRef,
        };
      });

      const fetchedImages = await Promise.all(imagePromises);
      // Sort by name (which often includes timestamp due to upload naming convention)
      // or we could try to fetch metadata if needed, but name is simpler for now.
      // Assuming names like '163...' (timestamp) or 'image_123...'
      fetchedImages.sort((a, b) => b.name.localeCompare(a.name));

      setImages(fetchedImages);
    } catch (error) {
      console.error("Error fetching images:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load images from storage.",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchImages();
  }, []);

  const handleDelete = async (image: StorageImage) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this image? This cannot be undone."
      )
    )
      return;

    setDeletingId(image.name);
    try {
      await deleteObject(image.ref);
      setImages((prev) => prev.filter((img) => img.name !== image.name));
      toast({
        title: "Image Deleted",
        description: "The image has been successfully deleted.",
      });
    } catch (error) {
      console.error("Error deleting image:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to delete image.",
      });
    } finally {
      setDeletingId(null);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({
        title: "Copied!",
        description: "Image URL copied to clipboard.",
      });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to copy URL.",
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-forest-green/10 flex items-center justify-center">
            <ImageIcon className="h-5 w-5 text-forest-green" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-forest-green">
              Newsletter Images
            </h1>
            <p className="text-sm text-gray-500">
              Manage images uploaded for newsletters
            </p>
          </div>
        </div>
        <Button
          onClick={fetchImages}
          variant="outline"
          className="border-forest-green text-forest-green hover:bg-forest-green/5"
          disabled={loading}
        >
          {loading ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="mr-2 h-4 w-4" />
          )}
          Refresh
        </Button>
      </div>

      {loading && images.length === 0 ? (
        <div className="flex justify-center items-center py-20">
          <Loader2 className="h-8 w-8 text-forest-green animate-spin" />
        </div>
      ) : images.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-lg border border-gray-200">
          <ImageIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No images found in newsletter storage.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {images.map((image) => (
            <div
              key={image.name}
              className="group bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
            >
              <div className="aspect-video w-full bg-gray-100 relative overflow-hidden">
                <img
                  src={image.url}
                  alt={image.name}
                  className="w-full h-full object-cover transition-transform group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    className="h-8 w-8 p-0"
                    onClick={() => window.open(image.url, "_blank")}
                    title="View Full Size"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    className="h-8 w-8 p-0"
                    onClick={() => copyToClipboard(image.url)}
                    title="Copy URL"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div className="p-3">
                <p className="text-sm font-medium text-gray-700 truncate mb-3" title={image.name}>
                  {image.name}
                </p>
                <Button
                  variant="destructive"
                  size="sm"
                  className="w-full"
                  onClick={() => handleDelete(image)}
                  disabled={deletingId === image.name}
                >
                  {deletingId === image.name ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete
                    </>
                  )}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default NewsletterImages;
